import React, { useEffect, useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import BaseForm from '../../../../../../../components/shared/form-controls/base-form';
import { FormInput } from '../../../../../../../components/shared/form-controls/form-input';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/hooks';
import { createWorkCalendar, getRegions } from '../../../../../../../features/settings/workcalendar';
import { WorkCalendar } from '../../../../../../../features/settings/workcalendar/types';
import { workHoursStringArray } from '../../../types';
import { getHours, getRegion } from '../functions';
import { EditForm } from '../types';
import { useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '../../../../../../../routes/NavigationRoute';
import { DropdownField } from '../../../../../../../components/shared/nextgen/dropdown-field';
import { Lx } from '../../../../../../../i18n/consts';
import { FormCheckbox } from '../../../../../../../components/shared/form-controls/form-checkbox';
import { canManageWorkCalendars } from '../../../../../../../features/profile/entityPermissions';

const CreateWorkCalendarForm = (): JSX.Element | null => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const profile = useAppSelector(store => store.profile);
    const regions = useAppSelector(store => store.workCalendar.regions);
    const canEditWorkCalendars = canManageWorkCalendars(profile);

    const initialValues: EditForm = useMemo(() => {
        return {
            name: '',
            region: getRegion(270, regions) ?? regions[0],
            workHours: getHours(8),
            shortworkHours: getHours(7),
            isRoot: false
        };
    }, [regions]);

    useEffect(() => {
        if (!canEditWorkCalendars) {
            navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR, { replace: true });
            return;
        }

        if (!regions.length) {
            dispatch(getRegions());
        }
    }, [canEditWorkCalendars, dispatch, navigate, regions.length]);

    if (!canEditWorkCalendars) {
        return null;
    }

    const validationSchema: Yup.Schema<EditForm> = Yup.object().shape({
        name: Yup.string()
            .max(45, 'Must be 45 characters or less')
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.WORK_CALENDAR }) })),
        region: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required()
        }).required(),
        workHours: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required()
        }).required(),
        shortworkHours: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required()
        }).required(),
        isRoot: Yup.boolean().required()
    });

    const save = async (values: EditForm) => {
        const newCalendar: WorkCalendar = {
            id: 0,
            name: values.name,
            region: values.region.id,
            workHours: values.workHours.id,
            shortWorkHours: values.shortworkHours.id,
            isRoot: values.isRoot
        };

        await dispatch(createWorkCalendar({ workCalendar: newCalendar })).unwrap();
    };

    const handleClose = () => {
        navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR);
    };

    return (
        <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={save}>
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='create'
                    formHeader={Lx.Calendar.CREATE_WORK_CALENDAR}
                    savingTitle={intl.formatMessage({ id: Lx.General.WORK_CALENDAR })}
                    onClose={handleClose}
                    onAfterSave={handleClose}>
                    <div style={{ width: '100%', maxWidth: '500px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <FormInput required label={intl.formatMessage({ id: 'calendarName' })} name='name' />
                        <DropdownField
                            required
                            label={intl.formatMessage({ id: Lx.Calendar.REGION })}
                            name='region'
                            items={regions}
                            description={intl.formatMessage({ id: Lx.Calendar.EDIT_CALENDAR_DESC })}
                        />
                        <DropdownField
                            required
                            label={intl.formatMessage({ id: Lx.Calendar.WORKHOURS_IN_DAY })}
                            name='workHours'
                            items={workHoursStringArray}
                            description={intl.formatMessage({ id: Lx.Calendar.EDIT_CALENDAR_DESC })}
                        />
                        <DropdownField
                            required
                            label={intl.formatMessage({ id: Lx.Calendar.WORKHOURS_IN_SHORT_DAY })}
                            name='shortworkHours'
                            items={workHoursStringArray}
                            description={intl.formatMessage({ id: Lx.Calendar.EDIT_CALENDAR_DESC })}
                        />
                        <FormCheckbox name='isRoot' label={intl.formatMessage({ id: Lx.Calendar.PRIMARY })} />
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
};

export default React.memo(CreateWorkCalendarForm);
